import React from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { TimerMode } from '../TimerEffect';

interface PulseEffectProps {
  progress: number;
  size: number;
  color: string;
  secondaryColor: string;
  opacity: number;
  speed: number;
  mode: TimerMode;
}

const PulseEffect: React.FC<PulseEffectProps> = ({
  progress,
  size,
  color,
  secondaryColor,
  opacity,
  speed,
  mode
}) => {
  // 动画控制
  const controls = useAnimationControls();
  
  // 计算脉冲频率和强度（基于进度和模式）
  const isCountdown = mode === 'countdown';
  
  // 根据进度调整脉冲频率（越接近结束越快）
  const pulseFrequency = isCountdown
    ? Math.max(0.5, 2 - progress * 1.5) // 倒计时：越接近0脉冲越快
    : Math.max(0.5, 0.5 + progress); // 秒表：随时间增加脉冲频率
    
  // 脉冲强度
  const pulseIntensity = isCountdown 
    ? Math.min(1.2, 0.8 + (1 - progress) * 0.4) // 倒计时：越接近0强度越大
    : Math.min(1.2, 0.8 + progress * 0.4); // 秒表：随时间增加强度
  
  // 启动脉冲动画
  React.useEffect(() => {
    controls.start({
      scale: [1, pulseIntensity, 1],
      opacity: [opacity * 0.7, opacity, opacity * 0.7],
      transition: {
        duration: 1 / (pulseFrequency * speed),
        repeat: Infinity,
        ease: "easeInOut"
      }
    });
  }, [controls, opacity, pulseFrequency, pulseIntensity, speed]);
  
  // 计算圆圈大小（基于进度）
  const circleSize = isCountdown
    ? size * (0.4 + progress * 0.6) // 倒计时：随进度减小
    : size * (0.4 + (1 - progress) * 0.6); // 秒表：随进度增大
  
  // 内圆和外圆的颜色
  const innerColor = color;
  const outerColor = secondaryColor;
  
  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 外部脉冲圆 */}
      <motion.div
        animate={controls}
        style={{
          position: 'absolute',
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${innerColor}00 30%, ${outerColor}80 100%)`,
          opacity: opacity * 0.8,
        }}
      />
      
      {/* 内部核心圆 */}
      <div
        style={{
          width: circleSize * 0.5,
          height: circleSize * 0.5,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${innerColor} 0%, ${innerColor}40 100%)`,
          opacity: opacity,
          boxShadow: `0 0 ${size/10}px ${innerColor}`
        }}
      />
      
      {/* 进度指示器（可选）- 环绕核心的点 */}
      {progress > 0 && progress < 1 && Array.from({ length: 8 }).map((_, index) => {
        const angle = (Math.PI * 2 * index) / 8;
        const dotProgress = isCountdown ? progress : 1 - progress;
        const dotRadius = size * 0.35; // 点的旋转半径
        const dotSize = size * 0.03; // 点的大小
        const dotOpacity = index / 8 < dotProgress ? 0.9 : 0.2; // 已完成的点更亮
        
        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              width: dotSize,
              height: dotSize,
              borderRadius: '50%',
              backgroundColor: innerColor,
              opacity: dotOpacity * opacity,
              left: size / 2 + Math.cos(angle) * dotRadius - dotSize / 2,
              top: size / 2 + Math.sin(angle) * dotRadius - dotSize / 2,
              transition: 'opacity 0.3s ease',
            }}
          />
        );
      })}
    </div>
  );
};

export default PulseEffect; 